/**
 * The plan, what it allows, and how to change it.
 *
 * Two plans, so this is not a pricing table: it is a statement of which one the
 * business is on and what the other would change. The difference is one line — the
 * mark on the documents — and everything else is a ceiling, so the comparison is
 * ceilings side by side rather than ticks against features nobody has heard of.
 *
 * The one thing this screen never does is *enforce* anything. A limit is refused by
 * the API, at the moment of the work, with a message and the plan to offer; this
 * screen only explains. A limit enforced in an interface is a limit that is not
 * enforced.
 */
import { useEffect, useRef, useState } from "react";
import { api, helpUrl, legalUrl } from "../api.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Bar, Button, Card, Chip, Note, bytes, count, money, when } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";
import { DeleteAccount } from "./DeleteAccount.js";

/** A plan somebody chose before they got here — the pricing page's "Choose Pro". */
export interface WantedPlan {
  plan: string;
  /** Null when the address named a plan and not how often to pay: they choose here. */
  cadence: "monthly" | "annual" | null;
}

/** Yearly against twelve months, rounded down so the saving is never overstated. */
function yearlySaving(monthly: { amount: number } | null, annual: { amount: number } | null): number | null {
  if (!monthly || !annual) return null;
  const saved = monthly.amount * 12 - annual.amount;
  return saved > 0 ? Math.floor((saved / (monthly.amount * 12)) * 100) : null;
}

export function Account({ justPaid, wanted = null }: { justPaid: boolean; wanted?: WantedPlan | null }) {
  const { view, refresh } = useWorkspace();
  const [busy, setBusy] = useState<string | null>(null);
  const [cadence, setCadence] = useState<"monthly" | "annual">(wanted?.cadence ?? "monthly");
  const [closing, setClosing] = useState(false);

  const admin = view.role === "admin";
  const paid = view.plan.paid;
  const other = view.plans.find((p) => !p.current);
  // Held in state, because the address loses `?plan=` as soon as it has been read.
  const [asked] = useState(() => (wanted ? (view.plans.find((p) => p.id === wanted.plan && p.paid) ?? null) : null));

  const buy = async (planId: string, chosen: "monthly" | "annual" = cadence) => {
    setBusy(planId);
    try {
      const { url } = await api.checkout(planId, chosen);
      // A real navigation: Creem's checkout is a page, not a component, and a card
      // number should never come near this application.
      window.location.href = url;
    } catch (e) {
      setBusy(null);
      reportFailure(e, "We couldn't start the checkout.");
    }
  };

  /*
   * Somebody pressed "Choose Pro — monthly" or "— yearly" on the pricing page, signed
   * in (or signed up and named their business) and arrived here. An admin on another
   * plan goes straight to the checkout they chose; anybody else is told why not, above.
   *
   * Only when the choice was made. An address naming a plan and no cadence — an old
   * link, a bookmark — stops here with both prices on screen, rather than being sent to
   * a monthly checkout the person never picked.
   *
   * The parameter comes off the address first, so the back button from Creem's page —
   * or a reload — lands on this screen rather than starting a second checkout.
   */
  const started = useRef(false);
  useEffect(() => {
    if (!wanted || started.current) return;
    started.current = true;
    go("/account", { replace: true });
    if (asked && admin && !asked.current && wanted.cadence) void buy(asked.id, wanted.cadence);
    // Once, on arrival: nothing later should start a checkout on its own.
  }, []);

  const manage = async () => {
    setBusy("portal");
    try {
      const { url } = await api.portal();
      window.location.href = url;
    } catch (e) {
      setBusy(null);
      reportFailure(e, "We couldn't open your billing page.");
    }
  };

  return (
    <div className="wrap stack" style={{ gap: 24 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">Your account</span>
          <h1>{view.workspace.name}</h1>
          <p className="head__lede">
            On the {view.plan.name} plan.{" "}
            {view.plan.marked
              ? "Documents carry a small BroadPaper line at the foot of each page."
              : "Documents carry no BroadPaper mark."}
          </p>
        </div>
      </div>

      {/*
        The one screen where somebody may have just paid. Saying so — and saying
        that it can take a moment — is the difference between a happy customer and
        a customer refreshing and wondering.
      */}
      {justPaid ? (
        <Note tone="good" title="Thank you — your payment went through">
          It can take a few seconds for the plan to change here. <Button size="sm" variant="quiet" onClick={() => void refresh()}>Check again</Button>
        </Note>
      ) : null}

      {view.plan.inGrace ? (
        <Note tone="ask" title="Your last payment did not go through">
          Your plan is still being honoured for a few days. {admin ? "Updating your card keeps it that way." : "Ask an admin of this workspace to update the card."}
        </Note>
      ) : null}

      {asked ? (
        asked.current ? (
          <Note tone="good" title={`This workspace is already on ${asked.name}`}>
            There is nothing to buy. Everything it includes is already switched on.
          </Note>
        ) : !admin ? (
          <Note tone="info" title={`Only an admin can move this workspace to ${asked.name}`}>
            You can use everything else. <a href="/people">See who the admins are</a>, and ask one of them to choose {asked.name} here.
          </Note>
        ) : busy === asked.id ? (
          <Note tone="info" title="Taking you to the checkout…">
            Payment is taken by Creem, on their own page.
          </Note>
        ) : !wanted?.cadence ? (
          <Note tone="ask" title={`Choose how to pay for ${asked.name}`}>
            Monthly or yearly — yearly works out cheaper. Pick one under Plans below and press Move to {asked.name}.
          </Note>
        ) : null
      ) : null}

      {/*
        When the paid period ends, said whenever there is one — not only once the plan
        is set to stop. "Renews on" is the date a card is next charged, which is the
        date somebody on a paid plan most often wants to know.
      */}
      {view.plan.paid && view.plan.renewsAt && !view.plan.cancelAtPeriodEnd && !view.plan.inGrace ? (
        <p className="small muted" style={{ margin: 0 }}>
          Renews on {when(view.plan.renewsAt)}.
        </p>
      ) : null}

      {view.plan.cancelAtPeriodEnd && view.plan.renewsAt ? (
        <Note tone="info" title={`Your plan runs until ${when(view.plan.renewsAt)}`}>
          It will not renew. Everything you have made stays yours and stays downloadable; new documents go back to carrying the BroadPaper line after that
          date.
        </Note>
      ) : null}

      <Card>
        <div className="card__head">
          <strong>What you have used</strong>
          <span className="small muted">Counts reset on the first of the month</span>
        </div>
        <div className="card__body grid grid--2">
          <Usage label="Documents this month" used={view.usage.reportsThisMonth} total={view.limits.reportsPerMonth} format={count} />
          <Usage label="Storage" used={view.usage.storageBytes} total={view.limits.storageBytes} format={bytes} />
          <Usage label="Sets of data" used={view.usage.datasets} total={view.limits.datasets} format={count} />
          <Usage label="Saved designs" used={view.usage.templates} total={view.limits.templates} format={count} />
          {view.limits.importsPerMonth > 0 ? (
            <Usage label="Document imports this month" used={view.usage.importsThisMonth} total={view.limits.importsPerMonth} format={count} />
          ) : null}
        </div>
      </Card>

      <section className="stack" style={{ gap: 14 }}>
        <div className="spread" style={{ flexWrap: "wrap" }}>
          <h2>Plans</h2>
          {other?.paid && other.monthly && other.annual ? (
            <div className="row" style={{ gap: 4 }}>
              <Button size="sm" variant={cadence === "monthly" ? "ink" : "quiet"} onClick={() => setCadence("monthly")}>
                Monthly
              </Button>
              <Button size="sm" variant={cadence === "annual" ? "ink" : "quiet"} onClick={() => setCadence("annual")}>
                {yearlySaving(other.monthly, other.annual) ? `Yearly · save ${yearlySaving(other.monthly, other.annual)}%` : "Yearly"}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="grid grid--2">
          {view.plans.map((plan) => {
            const yearly = cadence === "annual" && plan.annual;
            const price = yearly ? plan.annual : plan.monthly;
            const per = !plan.paid ? "" : yearly ? " a year" : " a month";
            return (
              <Card key={plan.id} className={plan.current ? "" : undefined}>
                <div className="card__body stack" style={{ gap: 14 }}>
                  <div className="spread">
                    <span className="eyebrow">{plan.name}</span>
                    {plan.current ? <Chip tone="good">Your plan</Chip> : null}
                  </div>
                  <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                    <strong style={{ fontFamily: "var(--font-display)", fontSize: "2.2rem", lineHeight: 1 }}>
                      {/* A dash, not a guess, when the service could not ask Creem. */}
                      {!plan.paid ? "Free" : price ? money(price) : "—"}
                    </strong>
                    <span className="muted">{per}</span>
                  </div>
                  {/* Yearly named on the card while Monthly is selected, so the cheaper way to pay is never out of sight. */}
                  {plan.paid && !yearly && plan.monthly && plan.annual ? (
                    <span className="small muted" style={{ marginTop: -8 }}>
                      or {money(plan.annual)} a year
                      {yearlySaving(plan.monthly, plan.annual) ? ` — save ${yearlySaving(plan.monthly, plan.annual)}%` : ""}
                    </span>
                  ) : null}
                  <ul className="stack small" style={{ gap: 7, listStyle: "none", padding: 0, margin: 0 }}>
                    <li>
                      <strong>{plan.marked ? "Documents carry the BroadPaper line" : "No BroadPaper mark on your documents"}</strong>
                    </li>
                    <li className="muted">{count(plan.limits.reportsPerMonth)} documents a month</li>
                    <li className="muted">
                      {count(plan.limits.datasets)} sets of data, {count(plan.limits.rowsPerDataset)} rows each
                    </li>
                    <li className="muted">{count(plan.limits.templates)} saved designs</li>
                    <li className="muted">
                      {plan.limits.importsPerMonth > 0 ? `${count(plan.limits.importsPerMonth)} Word or PDF imports a month` : "No Word or PDF import"}
                    </li>
                    <li className="muted">{bytes(plan.limits.storageBytes)} of storage</li>
                  </ul>
                  {plan.current ? (
                    paid && admin ? (
                      <Button onClick={() => void manage()} busy={busy === "portal"}>
                        Manage payment
                      </Button>
                    ) : (
                      <Button disabled>Your current plan</Button>
                    )
                  ) : plan.paid ? (
                    <Button variant="primary" onClick={() => void buy(plan.id)} busy={busy === plan.id} disabled={!admin} title={admin ? undefined : "Only an admin of this workspace can change the plan"}>
                      Move to {plan.name}
                    </Button>
                  ) : (
                    // Moving down is Creem's portal rather than a button here: it
                    // takes effect at the end of the period that has been paid for,
                    // and the portal is where the customer can see that date.
                    <Button onClick={() => void manage()} busy={busy === "portal"} disabled={!admin}>
                      Change or cancel
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {!admin ? (
          <Note tone="info">
            Only an admin of this workspace can change what it pays. You can use everything else. <a href="/people">See who the admins are</a>
          </Note>
        ) : null}

        <p className="small muted" style={{ margin: 0 }}>
          Payment is taken by Creem, our merchant of record. A paid plan renews until it is cancelled, and moving down or cancelling takes effect at the end
          of the period already paid for. See the{" "}
          <a href={legalUrl("/legal/terms/")} target="_blank" rel="noreferrer">
            terms
          </a>{" "}
          and the{" "}
          <a href={legalUrl("/legal/refunds/")} target="_blank" rel="noreferrer">
            refund policy
          </a>
          .
        </p>
      </section>

      <Note tone="info" title="Your data">
        Everything you bring in is stored privately and is not used to train anything. Documents you produce are visible only to this workspace, unless you
        make a share link to one. <a href={helpUrl("/support/account/#your-data")} target="_blank" rel="noreferrer">What BroadPaper keeps</a> ·{" "}
        <a href={legalUrl("/legal/privacy/")} target="_blank" rel="noreferrer">
          Privacy policy
        </a>{" "}
        ·{" "}
        <a href={legalUrl("/legal/cookies/")} target="_blank" rel="noreferrer">
          Cookie policy
        </a>
      </Note>

      {/*
        Deleting the account, at the foot of the page and in plain words.

        It used to be an email to support, which is a deletion that depends on
        somebody reading their inbox. Self-serve now, behind a form that shows what
        goes before anything does — see DeleteAccount.
      */}
      <Card flat className="danger-zone">
        <div className="card__body spread" style={{ flexWrap: "wrap", gap: 14 }}>
          <div className="stack" style={{ gap: 4, maxWidth: "62ch" }}>
            <strong>Delete your account</strong>
            <span className="small muted">
              Permanently deletes this workspace — every set of data, design, document and share link — cancels any subscription, and removes your sign-in. It
              can't be undone.
            </span>
          </div>
          <Button variant="danger" icon="trash" onClick={() => setClosing(true)}>
            Delete account…
          </Button>
        </div>
      </Card>

      {closing ? <DeleteAccount onClose={() => setClosing(false)} /> : null}
    </div>
  );
}

function Usage({ label, used, total, format }: { label: string; used: number; total: number; format: (n: number) => string }) {
  const share = total <= 0 ? 0 : used / total;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="spread">
        <span className="small muted">{label}</span>
        {share >= 0.8 ? <span className="small" style={{ color: share >= 1 ? "var(--red-deep)" : "var(--amber)" }}>{share >= 1 ? "Full" : "Nearly full"}</span> : null}
      </div>
      <strong className="num" style={{ fontSize: 17 }}>
        {format(used)}{" "}
        <span className="muted" style={{ fontWeight: 400, fontSize: 14 }}>
          of {format(total)}
        </span>
      </strong>
      <Bar used={used} total={total} />
    </div>
  );
}
